import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { portal as portalApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Markdown from '../components/Markdown';
import PortalActionItem from '../components/PortalActionItem';
import PortalActionForm from '../components/PortalActionForm';
import { formatDate, formatTime, MEETING_STATUSES, OPEN_STATUSES, todayString } from '../utils/portal';

const AGENDA_TEMPLATE = `## 1. Call to order

## 2. Review of last meeting's action items

## 3. Trail status

## 4. New business

## 5. Adjourn
`;

/** Markdown editor with a live preview tab. */
function MarkdownEditor({ value, onChange, placeholder, rows = 18 }) {
  const [preview, setPreview] = useState(false);

  return (
    <div className="portal-editor">
      <div className="portal-editor-tabs">
        <button className={!preview ? 'active' : ''} onClick={() => setPreview(false)} type="button">Write</button>
        <button className={preview ? 'active' : ''} onClick={() => setPreview(true)} type="button">Preview</button>
        <span className="portal-editor-hint">
          Markdown: <code>## heading</code>, <code>- bullet</code>, <code>**bold**</code>, <code>[link](url)</code>
        </span>
      </div>

      {preview ? (
        <div className="portal-editor-preview">
          {value?.trim() ? <Markdown>{value}</Markdown> : <p className="portal-muted">Nothing to preview yet.</p>}
        </div>
      ) : (
        <textarea
          className="portal-editor-input"
          rows={rows}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

export default function PortalMeetingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isChair } = useAuth();
  const canManage = isAdmin || isChair;

  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [message, setMessage] = useState(location.state?.flash || null); // e.g. "Minutes assembled…" from Review & record
  // ?tab=agenda (the calendar invite's link) pins the tab so it isn't
  // switched to minutes on load.
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [tab, setTab] = useState(requestedTab || 'agenda');

  const [editingAgenda, setEditingAgenda] = useState(false);
  const [editingMinutes, setEditingMinutes] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [draft, setDraft] = useState('');
  const [details, setDetails] = useState({});
  const [saving, setSaving] = useState(false);

  const [notifying, setNotifying] = useState(false);
  const [emailForm, setEmailForm] = useState(null); // { to, cc, replyTo, note, sending }

  const [showActionForm, setShowActionForm] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [volunteers, setVolunteers] = useState([]);
  const [segments, setSegments] = useState([]);
  const [hasNotes, setHasNotes] = useState(false); // any topic has raw notes from Run Meeting

  const load = useCallback(async () => {
    try {
      const data = await portalApi.getMeeting(id);
      setMeeting(data);
      // Land on whichever half of the record is live: minutes once they exist.
      if (!requestedTab) {
        setTab((prev) => (prev === 'agenda' && (data.minutes_md || data.minutes_published_at) ? 'minutes' : prev));
      }
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [id, requestedTab]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canManage) return;
    Promise.all([portalApi.listVolunteers(), portalApi.listSegments()])
      .then(([v, s]) => { setVolunteers(v); setSegments(s); })
      .catch((err) => setMessage(`Error: ${err.message}`));
    portalApi.getTopics(id)
      .then((r) => setHasNotes(r.topics.some((t) => (t.live_notes || '').trim())))
      .catch(() => {});
  }, [canManage, id]);

  const save = async (patch, done) => {
    setSaving(true);
    try {
      await portalApi.updateMeeting(id, patch);
      await load();
      if (done) done();
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const publish = async (shouldPublish) => {
    try {
      await portalApi.publishMinutes(id, shouldPublish);
      setMessage(shouldPublish ? 'Minutes published to all volunteers.' : 'Minutes retracted to draft.');
      await load();
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    }
  };

  const openEmailMinutes = async () => {
    setEmailForm({ to: '', cc: '', replyTo: '', note: '', sending: false });
    try {
      const r = await portalApi.minutesRecipients();
      setEmailForm((f) => (f ? {
        ...f,
        to: f.to || r.to.join(', '),
        cc: f.cc || r.cc.join(', '),
        replyTo: f.replyTo || r.reply_to || '',
      } : f));
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const sendMinutes = async () => {
    setEmailForm((f) => ({ ...f, sending: true }));
    try {
      const r = await portalApi.emailMinutes(id, emailForm.to, emailForm.cc, emailForm.note, emailForm.replyTo);
      setMessage(`Minutes PDF emailed to ${r.to.join(', ')}${r.cc.length ? `, cc ${r.cc.length} committee member${r.cc.length === 1 ? '' : 's'}` : ''}.`);
      setEmailForm(null);
      await load();
    } catch (err) {
      setMessage(`Error: ${err.message}`);
      setEmailForm((f) => (f ? { ...f, sending: false } : f));
    }
  };

  const sendTestToSelf = async () => {
    setNotifying(true);
    try {
      const result = await portalApi.notifyMeeting(id, { test: true });
      setMessage(
        result.sent
          ? `Test sent to you only (${result.recipients.join(', ')}). Nothing was sent to the committee, and this does not count as the real notice.`
          : `Test failed to send: ${result.failed.join(', ')}`
      );
      await load();
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setNotifying(false);
    }
  };

  const notifyVolunteers = async () => {
    const count = volunteers.length;
    const resend = !!meeting.notified_at;
    const ok = window.confirm(
      `${resend ? 'Re-send' : 'Send'} a calendar notice for "${meeting.title}" to ` +
      `${count} volunteer${count === 1 ? '' : 's'} and admin${count === 1 ? '' : 's'}?` +
      (resend
        ? '\n\nThis replaces the calendar entry from the previous notice rather than adding a second one.'
        : '')
    );
    if (!ok) return;

    setNotifying(true);
    try {
      const result = await portalApi.notifyMeeting(id);
      setMessage(
        result.failed.length
          ? `Sent to ${result.sent} of ${result.total}. Failed: ${result.failed.join(', ')}`
          : `Calendar notice sent to all ${result.sent} volunteers.`
      );
      await load();
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setNotifying(false);
    }
  };

  const removeMeeting = async () => {
    if (!window.confirm(
      `Delete "${meeting.title}"? Its agenda and minutes are lost. ` +
      `Action items survive and become unfiled.`
    )) return;
    try {
      await portalApi.deleteMeeting(id);
      navigate('/volunteer/meetings');
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    }
  };

  const saveAction = async (form) => {
    if (editingAction) await portalApi.updateAction(editingAction.id, form);
    else await portalApi.createAction({ ...form, meeting_id: id });
    setShowActionForm(false);
    setEditingAction(null);
    await load();
  };

  if (loading) return <div className="loading">Loading meeting…</div>;
  if (!meeting) {
    return (
      <div className="portal-page">
        <div className="alert alert-error">{message || 'Meeting not found'}</div>
        <Link to="/volunteer/meetings">← All meetings</Link>
      </div>
    );
  }

  const openActions = meeting.actions.filter((a) => OPEN_STATUSES.includes(a.status));
  const closedActions = meeting.actions.filter((a) => !OPEN_STATUSES.includes(a.status));

  return (
    <div className="portal-page">
      <div className="portal-header">
        <div>
          <p className="portal-subtitle"><Link to="/volunteer/meetings">← All meetings</Link></p>
          <h1>{meeting.title}</h1>
          <div className="portal-meeting-when">
            {formatDate(meeting.meeting_date)}
            {meeting.start_time && <> · {formatTime(meeting.start_time)}</>}
            {meeting.location && <> · {meeting.location}</>}
            {meeting.status !== 'scheduled' && (
              <> · <span className={`portal-badge badge-${meeting.status}`}>
                {MEETING_STATUSES.find((s) => s.value === meeting.status)?.label}
              </span></>
            )}
            {meeting.is_exception && (
              <> · <span className="portal-badge badge-exception">Off-cycle</span></>
            )}
          </div>
          {meeting.is_exception && meeting.exception_note && (
            <p className="portal-exception-note">{meeting.exception_note}</p>
          )}
          <p className="portal-notify-status">
            {meeting.notified_at
              ? <>📧 Calendar notice sent {new Date(meeting.notified_at).toLocaleString()}
                  {meeting.notified_by_name && <> by {meeting.notified_by_name}</>}</>
              : <>📧 No calendar notice sent yet</>}
          </p>
        </div>

        {canManage && (
          <div className="portal-header-links">
            <button className="btn-sm btn-secondary" onClick={() => { setDetails(meeting); setEditingDetails(true); }}>
              Edit Details
            </button>
            <Link className="btn-sm btn-primary" to={`/volunteer/meetings/${id}/run`}>▶ Run Meeting</Link>
            {/* Once the meeting has happened (or notes exist), go straight to
                Review & record and have Claude draft the empty boxes. */}
            {!meeting.minutes_published_at && (hasNotes || meeting.meeting_date <= todayString()) && (
              <Link className="btn-sm btn-primary" to={`/volunteer/meetings/${id}/review?draft=1`}>
                Draft Minutes with Claude
              </Link>
            )}
            <button className="btn-sm btn-secondary" onClick={sendTestToSelf} disabled={notifying}>
              {notifying ? 'Sending…' : 'Send Test to Me'}
            </button>
            <button className="btn-sm btn-primary" onClick={notifyVolunteers} disabled={notifying}>
              {notifying ? 'Sending…' : meeting.notified_at ? 'Re-send Invite' : 'Notify Volunteers'}
            </button>
            {isAdmin && <button className="btn-sm btn-danger" onClick={removeMeeting}>Delete</button>}
          </div>
        )}
      </div>

      {message && (
        <div className={`alert ${message.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}<button onClick={() => setMessage(null)}>✕</button>
        </div>
      )}

      {/* ── Edit meeting details ─────────────────────────────────── */}
      {editingDetails && (
        <div className="modal-overlay" onClick={() => setEditingDetails(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <form
              className="portal-action-form"
              onSubmit={(e) => {
                e.preventDefault();
                save({
                  title: details.title,
                  meeting_date: details.meeting_date,
                  start_time: details.start_time || '',
                  location: details.location || '',
                  exception_note: details.exception_note || '',
                  status: details.status,
                  attendees: details.attendees || '',
                }, () => setEditingDetails(false));
              }}
            >
              <h3>Meeting Details</h3>

              <label>
                Title
                <input type="text" value={details.title || ''} onChange={(e) => setDetails({ ...details, title: e.target.value })} />
              </label>

              <div className="portal-form-row">
                <label>
                  Date
                  <input type="date" value={details.meeting_date || ''} onChange={(e) => setDetails({ ...details, meeting_date: e.target.value })} />
                </label>
                <label>
                  Start time
                  <input type="time" value={(details.start_time || '').slice(0, 5)} onChange={(e) => setDetails({ ...details, start_time: e.target.value })} />
                </label>
                <label>
                  Status
                  <select value={details.status} onChange={(e) => setDetails({ ...details, status: e.target.value })}>
                    {MEETING_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </label>
              </div>

              <label>
                Location
                <input type="text" value={details.location || ''} onChange={(e) => setDetails({ ...details, location: e.target.value })} />
              </label>

              <label>
                Why off-cycle? <span className="field-hint">(shown only when the date isn't the 2nd Tuesday)</span>
                <input type="text" value={details.exception_note || ''} onChange={(e) => setDetails({ ...details, exception_note: e.target.value })} />
              </label>

              <label>
                Attendees <span className="field-hint">(roll call, free text)</span>
                <textarea rows={2} value={details.attendees || ''} onChange={(e) => setDetails({ ...details, attendees: e.target.value })} />
              </label>

              <div className="portal-form-actions">
                <button type="button" className="btn-sm btn-secondary" onClick={() => setEditingDetails(false)}>Cancel</button>
                <button type="submit" className="btn-sm btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Tabs ─────────────────────────────────────────────────── */}
      <div className="portal-tabs">
        <button className={tab === 'agenda' ? 'active' : ''} onClick={() => setTab('agenda')}>
          Agenda
        </button>
        <button className={tab === 'minutes' ? 'active' : ''} onClick={() => setTab('minutes')}>
          Minutes {meeting.minutes_published_at ? '' : meeting.minutes_md ? '(draft)' : ''}
        </button>
        <button className={tab === 'actions' ? 'active' : ''} onClick={() => setTab('actions')}>
          Action Items {meeting.actions.length > 0 && <span className="portal-tab-count">{meeting.actions.length}</span>}
        </button>
      </div>

      {/* ── Agenda ───────────────────────────────────────────────── */}
      {tab === 'agenda' && (
        <section className="portal-section">
          {editingAgenda ? (
            <>
              <MarkdownEditor
                value={draft}
                onChange={setDraft}
                placeholder="Write the agenda in Markdown…"
              />
              <div className="portal-form-actions">
                <button className="btn-sm btn-secondary" onClick={() => setEditingAgenda(false)}>Cancel</button>
                {!draft.trim() && (
                  <button className="btn-sm btn-secondary" onClick={() => setDraft(AGENDA_TEMPLATE)}>
                    Use standard template
                  </button>
                )}
                <button
                  className="btn-sm btn-primary"
                  disabled={saving}
                  onClick={() => save({ agenda_md: draft }, () => setEditingAgenda(false))}
                >
                  {saving ? 'Saving…' : 'Save Agenda'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="portal-section-head">
                <h2>
                  Agenda
                  {meeting.agenda_md && !meeting.agenda_published && (
                    <span className="portal-badge badge-draft">Draft</span>
                  )}
                </h2>
                {canManage && (
                  <div className="portal-header-links">
                    {meeting.agenda_md && (
                      <button
                        className="btn-sm btn-secondary"
                        onClick={() => save({ agenda_published: !meeting.agenda_published })}
                      >
                        {meeting.agenda_published ? 'Mark as draft' : 'Share with volunteers'}
                      </button>
                    )}
                    <button
                      className="btn-sm btn-primary"
                      onClick={() => { setDraft(meeting.agenda_md || AGENDA_TEMPLATE); setEditingAgenda(true); }}
                    >
                      {meeting.agenda_md ? 'Edit Agenda' : 'Write Agenda'}
                    </button>
                  </div>
                )}
              </div>

              {meeting.agenda_md
                ? <Markdown>{meeting.agenda_md}</Markdown>
                : <p className="portal-muted">No agenda has been written yet.</p>}
            </>
          )}
        </section>
      )}

      {/* ── Minutes ──────────────────────────────────────────────── */}
      {tab === 'minutes' && (
        <section className="portal-section">
          {editingMinutes ? (
            <>
              <MarkdownEditor
                value={draft}
                onChange={setDraft}
                placeholder="Write the minutes in Markdown…"
              />
              <div className="portal-form-actions">
                <button className="btn-sm btn-secondary" onClick={() => setEditingMinutes(false)}>Cancel</button>
                {!draft.trim() && meeting.agenda_md && (
                  <button className="btn-sm btn-secondary" onClick={() => setDraft(meeting.agenda_md)}>
                    Start from the agenda
                  </button>
                )}
                <button
                  className="btn-sm btn-primary"
                  disabled={saving}
                  onClick={() => save({ minutes_md: draft }, () => setEditingMinutes(false))}
                >
                  {saving ? 'Saving…' : 'Save Minutes'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="portal-section-head">
                <h2>
                  Minutes
                  {meeting.minutes_md && !meeting.minutes_published_at && (
                    <span className="portal-badge badge-draft">Draft</span>
                  )}
                </h2>
                {meeting.minutes_md && !canManage && (
                  <div className="portal-header-links">
                    <a className="btn-sm btn-secondary" href={portalApi.minutesPdfUrl(id)}>Download PDF</a>
                  </div>
                )}
                {canManage && (
                  <div className="portal-header-links">
                    {meeting.minutes_md && (
                      <>
                        <a className="btn-sm btn-secondary" href={portalApi.minutesPdfUrl(id)}>Download PDF</a>
                        <button className="btn-sm btn-secondary" onClick={openEmailMinutes}>Email PDF…</button>
                      </>
                    )}
                    {meeting.minutes_md && (
                      <button
                        className="btn-sm btn-secondary"
                        onClick={() => publish(!meeting.minutes_published_at)}
                      >
                        {meeting.minutes_published_at ? 'Retract to draft' : 'Publish Minutes'}
                      </button>
                    )}
                    {meeting.minutes_md_previous && !meeting.minutes_published_at && (
                      <button
                        className="btn-sm btn-secondary"
                        title="Swap back to the draft that the last 'Save as the minutes' replaced. You can swap again to undo."
                        onClick={() => {
                          if (window.confirm('Swap these minutes for the previous draft? You can swap back the same way.')) {
                            save({ restore_previous_minutes: true });
                          }
                        }}
                        disabled={saving}
                      >
                        Restore previous draft
                      </button>
                    )}
                    <button
                      className="btn-sm btn-primary"
                      onClick={() => { setDraft(meeting.minutes_md || ''); setEditingMinutes(true); }}
                    >
                      {meeting.minutes_md ? 'Edit Minutes' : 'Write Minutes'}
                    </button>
                  </div>
                )}
              </div>

              {meeting.minutes_published_at && (
                <p className="portal-muted">
                  Published {new Date(meeting.minutes_published_at).toLocaleString()}
                  {meeting.minutes_published_by_name && <> by {meeting.minutes_published_by_name}</>}
                </p>
              )}

              {meeting.minutes_emailed_at && (
                <p className="portal-muted">
                  PDF emailed to {meeting.minutes_emailed_to} on {new Date(meeting.minutes_emailed_at).toLocaleString()}
                  {meeting.minutes_emailed_by_name && <> by {meeting.minutes_emailed_by_name}</>}
                </p>
              )}

              {emailForm && (
                <div className="modal-overlay" onClick={() => !emailForm.sending && setEmailForm(null)}>
                  <div className="modal-content minutes-email" onClick={(e) => e.stopPropagation()}>
                    <h3>Email the minutes as a PDF</h3>
                    {!meeting.minutes_published_at && (
                      <p className="alert alert-error">These minutes aren't published yet — the PDF and subject will say “Draft”.</p>
                    )}
                    <label>To
                      <input type="text" value={emailForm.to} placeholder="Management company contact (set under Admin)"
                        onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })} />
                    </label>
                    <label>Cc
                      <textarea rows={3} value={emailForm.cc} placeholder="name@example.com, another@example.com"
                        onChange={(e) => setEmailForm({ ...emailForm, cc: e.target.value })} />
                    </label>
                    <label>Reply-to
                      <input type="email" value={emailForm.replyTo} placeholder="Where replies should go"
                        onChange={(e) => setEmailForm({ ...emailForm, replyTo: e.target.value })} />
                    </label>
                    <label>Note (optional)
                      <textarea rows={3} value={emailForm.note} placeholder="Shown above the standard message"
                        onChange={(e) => setEmailForm({ ...emailForm, note: e.target.value })} />
                    </label>
                    <p className="portal-muted">To is the management company contact from Admin settings; Cc is every active committee member. Edit either for this send. The reply-to is remembered for next time.
                      {' '}<a href={portalApi.minutesPdfUrl(id)}>Preview the PDF</a></p>
                    <div className="portal-form-actions">
                      <button className="btn-sm btn-secondary" disabled={emailForm.sending} onClick={() => setEmailForm(null)}>Cancel</button>
                      <button className="btn-sm btn-primary" disabled={emailForm.sending || !emailForm.to.trim()} onClick={sendMinutes}>
                        {emailForm.sending ? 'Sending…' : 'Send'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Compiled minutes carry their own roll call. */}
              {meeting.attendees && !(meeting.minutes_md || '').includes('**Present:**') && (
                <p className="portal-attendees"><strong>Present:</strong> {meeting.attendees}</p>
              )}

              {meeting.minutes_md
                ? <Markdown>{meeting.minutes_md}</Markdown>
                : <p className="portal-muted">Minutes haven't been written yet.</p>}
            </>
          )}
        </section>
      )}

      {/* ── Action items ─────────────────────────────────────────── */}
      {tab === 'actions' && (
        <section className="portal-section">
          <div className="portal-section-head">
            <h2>Action Items</h2>
            {canManage && (
              <button
                className="btn-sm btn-primary"
                onClick={() => { setEditingAction(null); setShowActionForm(true); }}
              >
                Add Action Item
              </button>
            )}
          </div>

          {meeting.actions.length === 0 ? (
            <p className="portal-muted">
              Nothing assigned from this meeting yet.
              {canManage && ' Add the first one above.'}
            </p>
          ) : (
            <>
              <div className="portal-action-list">
                {openActions.map((a) => (
                  <PortalActionItem
                    key={a.id}
                    action={a}
                    onChanged={load}
                    onEdit={(item) => { setEditingAction(item); setShowActionForm(true); }}
                    showMeeting={false}
                  />
                ))}
              </div>

              {closedActions.length > 0 && (
                <>
                  <h3 className="portal-closed-heading">Closed</h3>
                  <div className="portal-action-list portal-action-list-closed">
                    {closedActions.map((a) => (
                      <PortalActionItem
                        key={a.id}
                        action={a}
                        onChanged={load}
                        onEdit={(item) => { setEditingAction(item); setShowActionForm(true); }}
                        showMeeting={false}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </section>
      )}

      {showActionForm && (
        <div className="modal-overlay" onClick={() => { setShowActionForm(false); setEditingAction(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <PortalActionForm
              initial={editingAction}
              volunteers={volunteers}
              segments={segments}
              lockMeetingId={editingAction ? null : id}
              onSave={saveAction}
              onCancel={() => { setShowActionForm(false); setEditingAction(null); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
